import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import GoogleSignInButton from './GoogleSignInButton';
import EmailPasswordForm from './EmailPasswordForm';
import {
  Check, ChevronRight, ChevronDown, ChevronUp,
  Phone, Loader2, MapPin, Home, Mail,
} from 'lucide-react';

// ── Step config ───────────────────────────────────────────────────────────────
// Step 0 is the unauthenticated sign-in gate (not shown in progress bar)
const STEPS = [
  { id: 'login',    label: 'Sign In'       },
  { id: 'profile',  label: 'Your Profile'  },
  { id: 'property', label: 'Add Property'  },
];

const PROPERTY_TYPES = [
  { value: '',              label: 'Select type...' },
  { value: 'Detached',      label: 'Detached House' },
  { value: 'Semi-Detached', label: 'Semi-Detached' },
  { value: 'Townhouse',     label: 'Townhouse' },
  { value: 'Condo',         label: 'Condo / Apartment' },
  { value: 'Duplex',        label: 'Duplex' },
  { value: 'Other',         label: 'Other' },
];

const ROLES = ['Homeowner', 'Investor', 'Landlord', 'Other'];

// ── Slug helper ───────────────────────────────────────────────────────────────
const slugify = (str = '') =>
  str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '');

// ── Component ─────────────────────────────────────────────────────────────────
export default function OnboardingWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    handleAuthCallback,
    completeOnboarding,
    getToken,
    user,
    isAuthenticated,
    loading: authLoading,
  } = useAuth();

  const [currentStep, setCurrentStep] = useState(0);
  const [initLoading, setInitLoading]  = useState(true);
  const [stepError, setStepError]      = useState(null);
  const [submitting, setSubmitting]    = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);

  // Step 1 — profile
  const [profileForm, setProfileForm] = useState({ name: '', phone: '', role: '' });

  // Step 2 — property
  const [propertyForm, setPropertyForm] = useState({
    address: '',
    type: '',
    imageUrl: '',
    lat: null,
    lng: null,
    placeId: '',
    geocoded: false,
    // Financial fields (accordion)
    purchasePrice: '',
    purchaseDate: '',
    currentValue: '',
    mortgageBalance: '',
    interestRate: '',
    remainingTerm: '',
    monthlyPayment: '',
    propertyTax: '',
  });
  const [propertyError, setPropertyError]         = useState(null);
  const [propertySubmitting, setPropertySubmitting] = useState(false);
  const [detailsExpanded, setDetailsExpanded]     = useState(false);

  const autocompleteRef = useRef(null);
  const retryTimerRef   = useRef(null);

  // ── Init: handle OAuth token param ───────────────────────────────────────
  useEffect(() => {
    const init = async () => {
      const token = searchParams.get('token');
      if (token) {
        try {
          const userData = await handleAuthCallback(token);
          setProfileForm(prev => ({ ...prev, name: userData?.name || '' }));
          setCurrentStep(1); // OAuth callback → skip sign-in
        } catch {
          setStepError('Authentication failed. Please try logging in again.');
        }
      }
      setInitLoading(false);
    };
    init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Auto-skip sign-in step when auth resolves ─────────────────────────────
  useEffect(() => {
    if (authLoading || initLoading) return;
    if (isAuthenticated && user && currentStep === 0) {
      setProfileForm(prev => ({ ...prev, name: user.name || '' }));
      if (user.onboardingComplete) {
        navigate('/');
      } else {
        setCurrentStep(1);
      }
    }
  }, [isAuthenticated, user, authLoading, initLoading, currentStep, navigate]);

  // ── Google Places autocomplete — init when step 2 is shown ───────────────
  useEffect(() => {
    if (currentStep !== 2) return;

    const initAutocomplete = () => {
      if (!window.google?.maps?.places) {
        retryTimerRef.current = setTimeout(initAutocomplete, 250);
        return;
      }
      const input = document.getElementById('wizard-address-input');
      if (!input) return;

      autocompleteRef.current = new window.google.maps.places.Autocomplete(input, {
        fields: ['formatted_address', 'geometry', 'photos', 'place_id'],
      });

      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current.getPlace();
        if (!place.geometry) return;

        const imageUrl = place.photos?.[0]?.getUrl({ maxWidth: 1200 }) || '';
        setPropertyForm(prev => ({
          ...prev,
          address: place.formatted_address || '',
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
          placeId: place.place_id || '',
          imageUrl,
          geocoded: true,
        }));
        setPropertyError(null);
      });
    };

    initAutocomplete();
    return () => { if (retryTimerRef.current) clearTimeout(retryTimerRef.current); };
  }, [currentStep]);

  // ── Step 1: submit profile ────────────────────────────────────────────────
  const handleProfileSubmit = async () => {
    if (!profileForm.name.trim()) return;
    try {
      setSubmitting(true);
      setStepError(null);
      await completeOnboarding({
        name: profileForm.name,
        phone: profileForm.phone,
        role: profileForm.role,
      });
      setCurrentStep(2);
    } catch {
      setStepError('Could not save your profile. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Step 2: submit property ───────────────────────────────────────────────
  const handlePropertySubmit = async () => {
    if (!propertyForm.geocoded) {
      setPropertyError('Please select a property address from the dropdown.');
      return;
    }
    try {
      setPropertySubmitting(true);
      setPropertyError(null);

      const n = (v) => (v !== '' ? Number(v) : null);

      const payload = {
        name: propertyForm.address,
        address: propertyForm.address,
        type: propertyForm.type || null,
        imageUrl: propertyForm.imageUrl,
        lat: propertyForm.lat,
        lng: propertyForm.lng,
        placeId: propertyForm.placeId,
        currentValue: n(propertyForm.currentValue),
        valuation: {
          estimate: n(propertyForm.currentValue),
          trend: 'Stable',
          confidence: propertyForm.currentValue ? 'Medium' : 'Low',
          comps: [],
        },
        finances: {
          purchasePrice: n(propertyForm.purchasePrice),
          purchaseDate: propertyForm.purchaseDate || null,
          propertyTax: n(propertyForm.propertyTax),
        },
        mortgage: {
          balance: n(propertyForm.mortgageBalance),
          interestRate: n(propertyForm.interestRate),
          remainingYears: n(propertyForm.remainingTerm),
          monthlyPayment: n(propertyForm.monthlyPayment),
        },
      };

      const token = getToken();
      const res = await fetch('/api/properties', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || err.message || 'Failed to add property.');
      }

      navigate('/', { replace: true });
    } catch (err) {
      setPropertyError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setPropertySubmitting(false);
    }
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  if (initLoading || authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto" style={{ color: '#CC0202' }} />
          <p className="mt-3 text-gray-500 text-sm">Setting up your account...</p>
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  // Progress bar steps are profile + property (step 0 = sign-in is the gate)
  const progressSteps = STEPS.slice(1);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center py-12 px-4">

      {/* Logo */}
      <div className="mb-8 flex items-center gap-2">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center text-white text-sm font-bold"
          style={{ backgroundColor: '#CC0202' }}
        >
          HN
        </div>
        <span className="text-gray-900 font-semibold text-lg tracking-tight">Harbour Nest</span>
      </div>

      {/* Progress bar — hidden on sign-in step */}
      {currentStep > 0 && (
        <div className="flex items-center gap-3 mb-8">
          {progressSteps.map((step, index) => {
            const stepIndex = index + 1; // 1 = profile, 2 = property
            return (
              <React.Fragment key={step.id}>
                <div className="flex items-center gap-2">
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-colors"
                    style={
                      stepIndex < currentStep
                        ? { backgroundColor: '#CC0202', color: '#fff' }
                        : stepIndex === currentStep
                        ? { backgroundColor: '#CC0202', color: '#fff' }
                        : { backgroundColor: '#E5E7EB', color: '#6B7280' }
                    }
                  >
                    {stepIndex < currentStep ? <Check className="w-3.5 h-3.5" /> : index + 1}
                  </div>
                  <span
                    className="text-sm font-medium hidden sm:block"
                    style={{ color: stepIndex <= currentStep ? '#111827' : '#9CA3AF' }}
                  >
                    {step.label}
                  </span>
                </div>
                {index < progressSteps.length - 1 && (
                  <div
                    className="w-12 h-0.5 transition-colors"
                    style={{ backgroundColor: stepIndex < currentStep ? '#CC0202' : '#E5E7EB' }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {/* Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 w-full max-w-md">

        {/* ── Step 0: Sign In ──────────────────────────────────────────── */}
        {currentStep === 0 && (
          <div className="space-y-6">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Welcome to Harbour Nest</h1>
              <p className="text-gray-500 text-sm mt-1">Sign in to manage your property portfolio.</p>
            </div>

            {stepError && (
              <div className="p-3 bg-red-50 border border-red-100 text-red-600 rounded-lg text-sm">
                {stepError}
              </div>
            )}

            <GoogleSignInButton />

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-400">or</span>
              </div>
            </div>

            {/* Email toggle */}
            <button
              onClick={() => setShowEmailForm(!showEmailForm)}
              className="w-full flex items-center gap-2 px-4 py-3 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition text-sm"
            >
              <Mail className="w-4 h-4" />
              <span>Continue with email</span>
              {showEmailForm
                ? <ChevronUp className="w-4 h-4 ml-auto" />
                : <ChevronDown className="w-4 h-4 ml-auto" />
              }
            </button>

            {showEmailForm && (
              <div className="pt-2 border-t border-gray-100">
                <EmailPasswordForm mode="login" />
                <p className="mt-4 text-center text-sm text-gray-500">
                  New here?{' '}
                  <Link
                    to="/register"
                    className="font-medium hover:underline"
                    style={{ color: '#CC0202' }}
                  >
                    Create an account
                  </Link>
                </p>
              </div>
            )}
          </div>
        )}

        {/* ── Step 1: Profile ──────────────────────────────────────────── */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Your profile</h1>
              <p className="text-gray-500 text-sm mt-1">
                Tell us a bit about yourself to get started.
              </p>
            </div>

            {stepError && (
              <div className="p-3 bg-red-50 border border-red-100 text-red-600 rounded-lg text-sm">
                {stepError}
              </div>
            )}

            <div className="space-y-4">
              {/* Full name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full name <span style={{ color: '#CC0202' }}>*</span>
                </label>
                <input
                  type="text"
                  value={profileForm.name}
                  onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                  onKeyDown={(e) => e.key === 'Enter' && handleProfileSubmit()}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2"
                  style={{ '--tw-ring-color': '#CC0202' }}
                  placeholder="Your full name"
                  autoFocus
                />
              </div>

              {/* Email (read-only) */}
              {user?.email && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <div className="w-full border border-gray-100 rounded-lg px-3 py-2.5 text-sm bg-gray-50 text-gray-500">
                    {user.email}
                  </div>
                </div>
              )}

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone number
                  <span className="ml-1 text-gray-400 font-normal">(optional)</span>
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="tel"
                    value={profileForm.phone}
                    onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2"
                    style={{ '--tw-ring-color': '#CC0202' }}
                    placeholder="+1 416 555 1234"
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1">For important renewal and maintenance alerts</p>
              </div>

              {/* Role pills */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  I am a…
                  <span className="ml-1 text-gray-400 font-normal">(optional)</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {ROLES.map((role) => (
                    <button
                      key={role}
                      type="button"
                      onClick={() =>
                        setProfileForm({
                          ...profileForm,
                          role: profileForm.role === role ? '' : role,
                        })
                      }
                      className="px-3 py-1.5 rounded-full text-sm font-medium border transition"
                      style={
                        profileForm.role === role
                          ? { backgroundColor: '#CC0202', color: '#fff', borderColor: '#CC0202' }
                          : { backgroundColor: '#fff', color: '#374151', borderColor: '#D1D5DB' }
                      }
                    >
                      {role}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <button
              onClick={handleProfileSubmit}
              disabled={!profileForm.name.trim() || submitting}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-lg text-white text-sm font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: '#CC0202' }}
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>Continue <ChevronRight className="w-4 h-4" /></>
              )}
            </button>
          </div>
        )}

        {/* ── Step 2: Add Property ─────────────────────────────────────── */}
        {currentStep === 2 && (
          <div className="space-y-5">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Add your first property</h1>
              <p className="text-gray-500 text-sm mt-1">
                Start with the address — everything else is optional.
              </p>
            </div>

            {propertyError && (
              <div className="p-3 bg-red-50 border border-red-100 text-red-600 rounded-lg text-sm">
                {propertyError}
              </div>
            )}

            <div className="space-y-4">
              {/* Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Property address <span style={{ color: '#CC0202' }}>*</span>
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <input
                    id="wizard-address-input"
                    type="text"
                    className="w-full border border-gray-200 rounded-lg pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2"
                    style={{ '--tw-ring-color': '#CC0202' }}
                    placeholder="123 Main Street, Toronto, ON"
                    defaultValue={propertyForm.address}
                    autoComplete="off"
                    onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                  />
                </div>
                {propertyForm.geocoded ? (
                  <p className="text-xs mt-1 flex items-center gap-1" style={{ color: '#16A34A' }}>
                    <Check className="w-3 h-3" /> Location confirmed
                  </p>
                ) : (
                  <p className="text-xs text-gray-400 mt-1">Select a suggestion from the dropdown</p>
                )}
              </div>

              {/* Property type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Property type
                  <span className="ml-1 text-gray-400 font-normal">(optional)</span>
                </label>
                <select
                  value={propertyForm.type}
                  onChange={(e) => setPropertyForm({ ...propertyForm, type: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 bg-white"
                  style={{ '--tw-ring-color': '#CC0202' }}
                >
                  {PROPERTY_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              {/* Photo preview */}
              {propertyForm.imageUrl && (
                <div className="rounded-xl overflow-hidden border border-gray-100">
                  <img
                    src={propertyForm.imageUrl}
                    alt="Property preview"
                    className="w-full h-36 object-cover"
                  />
                </div>
              )}

              {/* Collapsible financial details */}
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <button
                  type="button"
                  onClick={() => setDetailsExpanded(!detailsExpanded)}
                  className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 transition"
                >
                  <span>Add more details</span>
                  {detailsExpanded
                    ? <ChevronUp className="w-4 h-4" />
                    : <ChevronDown className="w-4 h-4" />
                  }
                </button>

                {detailsExpanded && (
                  <div className="p-4 space-y-3">
                    {/* Purchase price + date */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Purchase price
                        </label>
                        <div className="relative">
                          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">$</span>
                          <input
                            type="number"
                            value={propertyForm.purchasePrice}
                            onChange={(e) => setPropertyForm({ ...propertyForm, purchasePrice: e.target.value })}
                            className="w-full border border-gray-200 rounded-lg pl-5 pr-2 py-2 text-sm focus:outline-none focus:ring-1"
                            style={{ '--tw-ring-color': '#CC0202' }}
                            placeholder="850,000"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Purchase date
                        </label>
                        <input
                          type="date"
                          value={propertyForm.purchaseDate}
                          onChange={(e) => setPropertyForm({ ...propertyForm, purchaseDate: e.target.value })}
                          className="w-full border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-1"
                          style={{ '--tw-ring-color': '#CC0202' }}
                        />
                      </div>
                    </div>

                    {/* Current value */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Current / estimated value
                      </label>
                      <div className="relative">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">$</span>
                        <input
                          type="number"
                          value={propertyForm.currentValue}
                          onChange={(e) => setPropertyForm({ ...propertyForm, currentValue: e.target.value })}
                          className="w-full border border-gray-200 rounded-lg pl-5 pr-2 py-2 text-sm focus:outline-none focus:ring-1"
                          style={{ '--tw-ring-color': '#CC0202' }}
                          placeholder="1,250,000"
                        />
                      </div>
                    </div>

                    <hr className="border-gray-100" />
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Mortgage</p>

                    {/* Balance + interest rate */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Balance owing
                        </label>
                        <div className="relative">
                          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">$</span>
                          <input
                            type="number"
                            value={propertyForm.mortgageBalance}
                            onChange={(e) => setPropertyForm({ ...propertyForm, mortgageBalance: e.target.value })}
                            className="w-full border border-gray-200 rounded-lg pl-5 pr-2 py-2 text-sm focus:outline-none focus:ring-1"
                            style={{ '--tw-ring-color': '#CC0202' }}
                            placeholder="620,000"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Interest rate
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            step="0.01"
                            value={propertyForm.interestRate}
                            onChange={(e) => setPropertyForm({ ...propertyForm, interestRate: e.target.value })}
                            className="w-full border border-gray-200 rounded-lg pl-2 pr-5 py-2 text-sm focus:outline-none focus:ring-1"
                            style={{ '--tw-ring-color': '#CC0202' }}
                            placeholder="5.25"
                          />
                          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">%</span>
                        </div>
                      </div>
                    </div>

                    {/* Remaining term + monthly payment */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Remaining term
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            value={propertyForm.remainingTerm}
                            onChange={(e) => setPropertyForm({ ...propertyForm, remainingTerm: e.target.value })}
                            className="w-full border border-gray-200 rounded-lg pl-2 pr-8 py-2 text-sm focus:outline-none focus:ring-1"
                            style={{ '--tw-ring-color': '#CC0202' }}
                            placeholder="22"
                          />
                          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">yrs</span>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Monthly payment
                        </label>
                        <div className="relative">
                          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">$</span>
                          <input
                            type="number"
                            value={propertyForm.monthlyPayment}
                            onChange={(e) => setPropertyForm({ ...propertyForm, monthlyPayment: e.target.value })}
                            className="w-full border border-gray-200 rounded-lg pl-5 pr-2 py-2 text-sm focus:outline-none focus:ring-1"
                            style={{ '--tw-ring-color': '#CC0202' }}
                            placeholder="3,400"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Annual property tax */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Annual property tax
                      </label>
                      <div className="relative">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">$</span>
                        <input
                          type="number"
                          value={propertyForm.propertyTax}
                          onChange={(e) => setPropertyForm({ ...propertyForm, propertyTax: e.target.value })}
                          className="w-full border border-gray-200 rounded-lg pl-5 pr-2 py-2 text-sm focus:outline-none focus:ring-1"
                          style={{ '--tw-ring-color': '#CC0202' }}
                          placeholder="6,500"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="pt-1">
              <button
                onClick={handlePropertySubmit}
                disabled={!propertyForm.geocoded || propertySubmitting}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-lg text-white text-sm font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#CC0202' }}
              >
                {propertySubmitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Home className="w-4 h-4" />
                    Add my property →
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Step counter (shown on steps 1 and 2) */}
      {currentStep > 0 && (
        <p className="mt-4 text-xs text-gray-400">
          Step {currentStep} of {STEPS.length - 1}
        </p>
      )}
    </div>
  );
}
